import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import colors from '../../styles/colors';
import type { Card, CardRarity, CardPpv } from '../../types/models/card';

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const ppvLabel = (ppv: CardPpv) => {
  const name = ppv.ppv_description?.name ?? null;
  // data formato DD-MM-YYYY → anno in posizione [2]
  const parts = (ppv.date ?? '').split('-');
  const year = parts.length === 3 ? (parts[0].length === 4 ? parts[0] : parts[2]) : '';
  if (!name) return year ? `PPV #${ppv.id} (${year})` : `PPV #${ppv.id}`;
  return year ? `${name} (${year})` : name;
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 16px;
  overflow-y: auto;
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 680px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 215, 0, 0.25);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const CardId = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
`;

const RarityBadge = styled.span<{ $rarity: CardRarity }>`
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid ${({ $rarity }) => RARITY_COLOR[$rarity]};
  color: ${({ $rarity }) => RARITY_COLOR[$rarity]};
  text-transform: capitalize;
`;

const Spacer = styled.div`flex: 1;`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  &:hover { color: #fff; }
`;

const Body = styled.div`
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

// ── Immagini ──
const ImagesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  padding: 4px 14px;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.2)')};
  background-color: ${({ $active }) => ($active ? 'rgba(255,215,0,0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.45)')};
  font-size: 13px;
  cursor: pointer;
`;

const CardImgWrapper = styled.div`
  width: 320px;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const NoImgText = styled.span`
  font-size: 12px;
  color: rgba(255,255,255,0.2);
`;

// ── Sezioni info ──
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  padding: 3px 10px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
`;

const ItemRow = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClickableRow = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  margin: -4px -6px;
  transition: background-color 0.15s, color 0.15s;
  &:hover {
    background-color: rgba(163, 27, 27, 0.15);
    color: #fff;
  }
`;

const ItemAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #333;
  flex-shrink: 0;
`;

const EmptyText = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
`;

const OwnedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background-color: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.4);
  border-radius: 6px;
  color: #5adf7a;
  font-size: 14px;
  font-weight: 600;
`;

const NotOwnedBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
`;

const NoteText = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  line-height: 1.5;
`;

const AuthorValue = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);

  em {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    font-style: normal;
    margin-left: 6px;
  }
`;

const AuthorLink = styled.span`
  font-size: 14px;
  color: rgba(255, 215, 0, 0.85);
  cursor: pointer;
  font-weight: 600;
  transition: color 0.15s;

  &:hover { color: gold; text-decoration: underline; }

  em {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    font-style: normal;
    font-weight: 400;
    margin-left: 6px;
    text-decoration: none;
  }
`;

const ReferenceLink = styled.a`
  font-size: 14px;
  color: #4a9eff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s;
  word-break: break-all;
  &:hover { color: #7bbfff; text-decoration: underline; }
`;

const ReferenceText = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  word-break: break-all;
`;

const ExpansionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
`;


// ─── Componente ───────────────────────────────────────────────────────────────

interface CardDetailModalProps {
  card: Card;
  ownedQuantity: number;
  isAuthenticated: boolean;
  onClose: () => void;
}

const CardDetailModal: React.FC<CardDetailModalProps> = ({
  card,
  ownedQuantity,
  isAuthenticated,
  onClose,
}) => {
  const [imgSide, setImgSide] = useState<'fronte' | 'retro'>('fronte');
  const navigate = useNavigate();

  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };

  const fronteSrc = toImgSrc(card.fronte_path);
  const retroSrc = toImgSrc(card.retro_path);
  const currentSrc = imgSide === 'fronte' ? fronteSrc : retroSrc;

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Dialog>
        <Header>
          <CardId>#{card.id}</CardId>
          <RarityBadge $rarity={card.rarity}>{card.rarity}</RarityBadge>
          <Spacer />
          <CloseBtn type="button" onClick={onClose}>✕</CloseBtn>
        </Header>

        <Body>
          {/* ── Immagine ── */}
          <ImagesSection>
            <ToggleRow>
              <ToggleBtn $active={imgSide === 'fronte'} onClick={() => setImgSide('fronte')}>
                Fronte
              </ToggleBtn>
              <ToggleBtn $active={imgSide === 'retro'} onClick={() => setImgSide('retro')}>
                Retro
              </ToggleBtn>
            </ToggleRow>
            <CardImgWrapper>
              {currentSrc
                ? <CardImg src={currentSrc} alt={`Carta #${card.id} ${imgSide}`} />
                : <NoImgText>Nessuna immagine</NoImgText>
              }
            </CardImgWrapper>
          </ImagesSection>

          {/* ── Possesso (solo se loggato) ── */}
          {isAuthenticated && (
            ownedQuantity > 0
              ? <OwnedBadge>In tuo possesso: {ownedQuantity} {ownedQuantity === 1 ? 'copia' : 'copie'}</OwnedBadge>
              : <NotOwnedBadge>Non possiedi questa carta</NotOwnedBadge>
          )}

          {/* ── Autore ── */}
          {(card.author_user || card.author_name) && (
            <Section>
              <SectionTitle>Autore</SectionTitle>
              <ItemRow>
                {card.author_user ? (
                  <AuthorLink onClick={() => goTo(`/carte/utente/${card.author_user!.username}`)}>
                    {card.author_user.username}
                    {card.author_user.nickname_forum && (
                      <em>"{card.author_user.nickname_forum}"</em>
                    )}
                  </AuthorLink>
                ) : (
                  <AuthorValue>{card.author_name}</AuthorValue>
                )}
              </ItemRow>
            </Section>
          )}

          {/* ── Wrestler ── */}
          <Section>
            <SectionTitle>Wrestler</SectionTitle>
            {(card.wrestlers ?? []).length === 0
              ? <EmptyText>Nessun wrestler associato</EmptyText>
              : (card.wrestlers ?? []).map((w) => (
                <ClickableRow key={w.id} onClick={() => goTo(`/wrestlers/i_${w.id}_d_${w.name}`)}>
                  {w.avatar_path && (
                    <ItemAvatar
                      src={toImgSrc(w.avatar_path) ?? undefined}
                      alt={w.name}
                    />
                  )}
                  {w.name}
                </ClickableRow>
              ))
            }
          </Section>

          {/* ── PPV ── */}
          <Section>
            <SectionTitle>PPV</SectionTitle>
            {(card.ppvs ?? []).length === 0
              ? <EmptyText>Nessun PPV associato</EmptyText>
              : (card.ppvs ?? []).map((p) => (
                <ClickableRow key={p.id} onClick={() => goTo(`/ppv/specific/i_${p.id}_d_${p.ppv_description?.name ?? ''}`)}>
                  {ppvLabel(p)}
                </ClickableRow>
              ))
            }
          </Section>

          {/* ── Cinture ── */}
          <Section>
            <SectionTitle>Cinture</SectionTitle>
            {(card.belts ?? []).length === 0
              ? <EmptyText>Nessuna cintura associata</EmptyText>
              : (card.belts ?? []).map((b) => (
                <ClickableRow key={b.id} onClick={() => goTo(`/belts/${b.id}/hall-of-fame`)}>
                  {b.name}
                </ClickableRow>
              ))
            }
          </Section>

          {/* ── Tag ── */}
          <Section>
            <SectionTitle>Tag</SectionTitle>
            {(card.tags ?? []).length === 0
              ? <EmptyText>Nessun tag</EmptyText>
              : (
                <TagsList>
                  {(card.tags ?? []).map((t) => (
                    <Tag key={t.id}>{t.name}</Tag>
                  ))}
                </TagsList>
              )
            }
          </Section>

          {/* ── Espansioni ── */}
          <Section>
            <SectionTitle>Espansioni</SectionTitle>
            {(card.expansions ?? []).length === 0
              ? <EmptyText>Nessuna espansione associata</EmptyText>
              : (card.expansions ?? []).map((exp) => (
                <ExpansionRow key={exp.id}>
                  <span>{exp.name}</span>
                </ExpansionRow>
              ))
            }
          </Section>

          {/* ── Riferimento ── */}
          {(card.reference_url || card.reference_string) && (
            <Section>
              <SectionTitle>Riferimento</SectionTitle>
              <ItemRow>
                {card.reference_url ? (
                  <ReferenceLink href={card.reference_url} target="_blank" rel="noopener noreferrer">
                    {card.reference_string ?? card.reference_url}
                  </ReferenceLink>
                ) : (
                  <ReferenceText>{card.reference_string}</ReferenceText>
                )}
              </ItemRow>
            </Section>
          )}

          {/* ── Note ── */}
          <Section>
            <SectionTitle>Note</SectionTitle>
            {card.notes
              ? <NoteText>{card.notes}</NoteText>
              : <EmptyText>Nessuna nota</EmptyText>
            }
          </Section>
        </Body>
      </Dialog>
    </Overlay>
  );
};

export default CardDetailModal;
